import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';

import { FINE_PRINT_CATEGORY_LABELS, FinePrintCategory, FinePrintItem } from '~/types/tours/tourOffer';

interface Props {
  items: Array<FinePrintItem>;
  onChange: (items: Array<FinePrintItem>) => void;
}

const NEW_ITEM: FinePrintItem = { title: '', category: 'information', body: '' };

export default function FinePrintEditor({ items, onChange }: Props) {
  const update = (index: number, change: Partial<FinePrintItem>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...change } : item)));
  const move = (from: number, to: number) => {
    const next = [...items];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <Stack spacing={1.5}>
      {items.length === 0 && (
        <Typography color="text.secondary">
          No fine print yet. Add what's not included, important information and anything else customers should know.
        </Typography>
      )}
      {items.map((item, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
              <TextField
                select
                size="small"
                label="Section"
                value={item.category}
                onChange={(event) => update(index, { category: event.target.value as FinePrintCategory })}
                sx={{ minWidth: 220 }}
              >
                {(Object.keys(FINE_PRINT_CATEGORY_LABELS) as Array<FinePrintCategory>).map((category) => (
                  <MenuItem key={category} value={category}>
                    {FINE_PRINT_CATEGORY_LABELS[category]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Title"
                value={item.title}
                onChange={(event) => update(index, { title: event.target.value })}
                error={!item.title.trim()}
                fullWidth
              />
              <Tooltip title="Move up">
                <span>
                  <IconButton aria-label="Move up" disabled={index === 0} onClick={() => move(index, index - 1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    aria-label="Move down"
                    disabled={index === items.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton
                  aria-label="Remove fine print"
                  onClick={() => onChange(items.filter((_, i) => i !== index))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <MarkdownEditor label="Details" showLabel value={item.body} onChange={(body) => update(index, { body })} />
            {!item.body.trim() && (
              <Typography variant="caption" color="error">
                Details are required
              </Typography>
            )}
          </Stack>
        </Paper>
      ))}
      <Box>
        <Button startIcon={<AddIcon />} onClick={() => onChange([...items, NEW_ITEM])}>
          Add fine print
        </Button>
      </Box>
    </Stack>
  );
}
